import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import {
  CameraIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

import { useImageCropper } from 'hooks/useImageCropper';
import ImageCropper from 'components/ImageCropper';
import { useAlert } from 'context/AlertContext';
import { validateFiles, uploadErrorMessage, IMAGE_ACCEPT } from 'utils/uploadValidation';

import useEasypayGate from 'hooks/useEasypayGate';
import { EasypayLockedHint } from 'components/EasypayPayout';
import { updatePromoter } from '../../../redux/actions/promoter';
import { updateBrandProfile } from '../../../redux/actions/brands';

const BoardHeader = ({ userType, info, section, setIsEditing, isEditing }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [savingAvatar, setSavingAvatar] = useState(false);
  const { blocked: easypayBlocked } = useEasypayGate();

  const {
    imagePreview,
    croppedImagePreview,
    isCropping,
    handleImageChange,
    handleCropSave,
    setIsCropping,
    resetImage,
  } = useImageCropper();

  const getButtonDetails = () => {
    if (['promoterInfo', 'brandInfo', 'info'].includes(section) && !isEditing) {
      return {
        buttonText: t('dashboard.edit_profile'),
        action: () => setIsEditing(true),
      };
    }
    if (section === 'myEvents' && userType === 'promoter') {
      // T2B-423: always usable — without Easypay the event is saved as a
      // draft. The "i" beside it says publishing waits for the account.
      return {
        buttonText: t('dashboard.create_event'),
        action: () => navigate('/create-event'),
        primary: true,
        hint: easypayBlocked,
      };
    }
    if (section === 'paymentDetails') {
      return {
        buttonText: t('dashboard.update_payment_info'),
        action: () => {},
      };
    }
    if (section === 'preferences') {
      return { buttonText: t('dashboard.edit_preferences'), action: () => {} };
    }
    return { buttonText: '', action: null };
  };

  const { buttonText, action, primary, hint } = getButtonDetails();

  const canEditAvatar =
    isEditing && ['promoterInfo', 'brandInfo', 'info'].includes(section);

  const dataUrlToBlob = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };
  const toBlob = async (out) => {
    if (out instanceof Blob || out instanceof File) return out;
    if (typeof out === 'string' && out.startsWith('data:image')) {
      return await dataUrlToBlob(out);
    }
    if (out?.blob instanceof Blob) return out.blob;
    if (typeof out?.toBlob === 'function') {
      return await new Promise((res) => out.toBlob(res, 'image/jpeg', 0.92));
    }
    return null;
  };

  const submitCroppedAvatar = async (output) => {
    if (!output || !info?.id) return;
    setSavingAvatar(true);
    try {
      const blob = await toBlob(output);
      if (!blob) throw new Error('Could not normalize crop output to Blob');

      const fd = new FormData();
      fd.append('avatar', blob, 'avatar.jpg');

      if (userType === 'brand') {
        await dispatch(updateBrandProfile(info.id, fd));
      } else {
        await dispatch(updatePromoter(fd));
      }

      resetImage();
      setIsCropping(false);
    } catch (e) {
      console.error('Avatar update failed:', e);
      const uploadMessage = uploadErrorMessage(e, t);
      showAlert({
        title: t('alert_dialog.edit.update_error_title'),
        message: uploadMessage || t('alert_dialog.edit.update_error_message'),
        primaryAction: {
          label: t('alert_dialog.edit.update_error_primary_action'),
        },
      });
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleAvatarPick = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors: uploadErrors } = validateFiles(files, {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showAlert({
        type: 'warning',
        title: t('upload.error_title', { defaultValue: 'Upload issue' }),
        message: uploadErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
      e.target.value = '';
      return;
    }
    if (accepted.length) handleImageChange(e);
  };

  useEffect(() => {
    if (!isEditing) {
      setIsCropping(false);
      resetImage();
    }
  }, [isEditing, resetImage, setIsCropping]);

  if (!info) return null;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white p-6 rounded-tr-3xl min-w-0">
        <div className="flex flex-col sm:flex-row items-center sm:items-center sm:space-x-6 text-center sm:text-left flex-1 min-w-0">
          <div className="relative shrink-0">
            <img
              src={
                croppedImagePreview ||
                info.avatar ||
                'https://via.placeholder.com/150'
              }
              alt="Avatar"
              className="h-20 w-20 object-cover rounded-full"
            />

            {canEditAvatar && (
              <>
                <label
                  htmlFor="dashboard-avatar-input"
                  className="absolute bottom-0 right-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white shadow cursor-pointer hover:bg-gray-50"
                  title={t('dashboard.change_avatar') || 'Change avatar'}
                >
                  {savingAvatar ? (
                    <ClipLoader color="#9CA3AF" size={16} />
                  ) : (
                    <CameraIcon className="w-4 h-4 text-gray-600" />
                  )}
                </label>
                <input
                  id="dashboard-avatar-input"
                  type="file"
                  accept={IMAGE_ACCEPT}
                  className="sr-only"
                  onChange={(e) => {
                    handleAvatarPick(e);
                    e.target.value = '';
                  }}
                  disabled={savingAvatar}
                />
              </>
            )}
          </div>

          <div className="min-w-0 w-full">
            <h1
              className="text-2xl sm:text-3xl font-bold text-gray-700 mt-4 sm:mt-0 max-w-full break-words sm:truncate"
              title={info.username}
            >
              {info.username}
            </h1>
          </div>
        </div>

        {buttonText && (
          <div className="self-end sm:self-auto shrink-0">
            {primary ? (
              <span className="inline-flex items-center gap-1.5">
                <button
                  onClick={action}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-bold shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 bg-yellow-500 hover:bg-yellow-400 active:bg-yellow-600 hover:shadow-lg hover:-translate-y-0.5"
                >
                  <PlusIcon className="w-4 h-4 stroke-[2.5] transition-transform duration-200 group-hover:rotate-90" />
                  {buttonText}
                </button>
                {hint && <EasypayLockedHint />}
              </span>
            ) : (
              <button
                onClick={action}
                className="text-gray-400 text-xs font-bold underline"
              >
                {buttonText}
              </button>
            )}
          </div>
        )}
      </div>

      {isCropping && imagePreview && (
        <ImageCropper
          imageSrc={imagePreview}
          aspect={1}
          shape="round"
          onCropSave={async (output) => {
            await handleCropSave(output);
            await submitCroppedAvatar(output);
          }}
          onClose={() => {
            setIsCropping(false);
            resetImage();
          }}
        />
      )}
    </>
  );
};

export default BoardHeader;
